import React from 'react';
import clsx from 'clsx';

import {
  getSheetData,
  getFileContent,
  mapFileContent,
  writeXLSFile,
} from '@utils';

import { RingCentralService } from '@consumers/services';

import parseTimezone from '../../utils';

import Button from '@material-ui/core/Button';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import LinearProgress from '@material-ui/core/LinearProgress';
import Tooltip from '@material-ui/core/Tooltip';

import CloudUpload from '@material-ui/icons/CloudUpload';
import AlbumIcon from '@material-ui/icons/Album';

import { COLUMN_HEADERS, EXTENSION_TYPE } from '@shared/constants';

import UploadDialog from './upload-dialog';
import ExtensionGrid from './extension-grid';
import SyncSuccessComponent from './sync-success';
import SyncErrorComponent from './sync-error';
import ErrorList from './error-list';

import useStyles from './style';

import { toast } from 'react-toastify';

const paperElevation = 1;
const noop = () => {};
let shouldSyncPause = false;
const defaultState = {
  openUploadDialog: false,
  selectedFile: null,
  dataBeingUploaded: [],
  uploadedData: [],
  gridData: [],
  pageSize: 20,
  pageNumber: 1,
  isLoading: false,
  isSyncingRecords: false,
  errors: [],
  uploadedPercentage: 0,
  showSuccessModal: false,
  showErrorModal: false,
  isErrorExpanded: true,
  showProgressBar: false,
  sortObj: null,
};

const NUMBER_TYPE_COLUMNS = ['MailboxID'];
const BOOLEAN_TYPE_COLUMNS = ['SendWelcomeEmail'];

const UploadData = ({
  accountId = null,
  handleJwksResponse = noop,
  onReset = noop,
  getJWKSToken = noop,
  extensionType = EXTENSION_TYPE.LIMITED,
}) => {
  const classes = useStyles();
  const [state, setState] = React.useState(defaultState);

  const onSyncConfirm = async () => {
    setState(prevState => ({
      ...prevState,
      isSyncingRecords: true,
      showProgressBar: true,
    }));
    const extensionData = state.dataBeingUploaded;
    //const extensionData = state.gridData;
    await updateExtensionsFromExcel(extensionData);
  };

  const updateDevicesFromExcel = async (info = {}, index) => {
    if (!info.Street || !info.PhoneID) {
      return;
    }

    const deviceData = {
      emergencyServiceAddress: {
        street: info.Street,
        street2: info.Street2,
        city: info.City,
        state: info.State,
        country: info.Country,
        zip: info.ZipCode?.toString(),
        customerName: info.CustomerName,
      },
    };

    const response = await RingCentralService.updateDevice(
      deviceData,
      accountId,
      info?.PhoneID?.toString(),
      getJWKSToken()
    );

    handleJwksResponse(response, (data, error) => {
      if (error) {
        let errors = state.errors;
        if (error.length && !error[0]?.message?.includes('Internal')) {
          errors.push({
            message: `Issue updating device ID ${info.PhoneID} for user extension ${info.Extension} : ${error[0]?.message}`,
            row: index + 1,
          });
        }
        setState(prevState => ({
          ...prevState,
          errors,
        }));
      }
    });
  };

  const updateExtensionsFromExcel = async (extensionData = []) => {
    for (let i = 0; i < extensionData.length; i++) {
      if (shouldSyncPause) {
        break;
      }

      let info = extensionData[i];
      //UPDATE-DEVICE-DATA
      await updateDevicesFromExcel(info, i);

      const body = {
        extensionNumber: info?.Extension?.toString(),
        contact: {
          firstName: info.FirstName,
          lastName: info.LastName ? info.LastName : '',
          email: info.Email,
          pronouncedName: {
            type: 'Default',
          },
        },
        name: info.ExtensionName,
        regionalSettings: {
          timezone: {
            id: parseTimezone(info.Timezone),
          },
        },
        transition: {
          sendWelcomeEmail:
            info.SendWelcomeEmail == true ||
            String(info.SendWelcomeEmail).toLowerCase() == 'true'
              ? true
              : false,
        },
      };

      // Rather than setting timezones to US/Eastern when no timezone is supplied,
      // just delete the regionalSettings object so it doesn't get updated.
      if (!info.Timezone || info.Timezone == '') {
        delete body.regionalSettings;
      }

      if (extensionType === EXTENSION_TYPE.LIMITED) {
        if (info.Password && info.Pin) {
          body.password = info.Password.toString();
          body.ivrPin = info.Pin.toString();
          body.status = 'Enabled';
        } else if (info.Pin && !info.Password) {
          body.ivrPin = info.Pin.toString();
          body.status = 'Enabled';
        } else if (!info.Pin && info.Password) {
          body = undefined;
        }
      } else if (extensionType === EXTENSION_TYPE.USER) {
        if (info.Password && info.Pin) {
          body.password = info.Password.toString();
          body.ivrPin = info.Pin.toString();
        } else if (info.Pin && !info.Password) {
          body.ivrPin = info.Pin.toString();
        } else if (!info.Pin && info.Password) {
          body.password = info.Password.toString();
        }
        if (info.Status) {
          body.status = info.Status;
        }
      }

      //UPDATE-NOTIFICATION-ENDPOINT
      let extResponse = await RingCentralService.updateExtension(
        body,
        accountId,
        info.MailboxID.toString(),
        getJWKSToken()
      );
      handleJwksResponse(extResponse, async (data, error) => {
        if (error) {
          let errors = state.errors;
          if (error.length && !error[0]?.message?.includes('Internal')) {
            errors.push({
              message: `Issue updating user extension ${info.Extension} : ${error[0]?.message}`,
              row: i + 1,
            });
          }
          setState(prevState => ({
            ...prevState,
            errors,
          }));
        } else {
          // console.log(
          //   "Extension info successfully changed for " +
          //     info.Extension.toString()
          // );
        }
      });

      // GET-CURRENT-NOTIFICATION-ENDPOINT
      let currentNotificationSettings = {};
      const getResponse = await RingCentralService.getNotificationSetting(
        accountId,
        info.MailboxID.toString(),
        getJWKSToken()
      );
      handleJwksResponse(getResponse, (data, error) => {
        if (error) {
          let errors = state.errors;
          if (error.length && !error[0]?.message?.includes('Internal')) {
            errors.push({
              message: `Issue pulling notification settings for ${info.Extension} : ${error[0]?.message}`,
              row: i + 1,
            });
          }
          setState(prevState => ({
            ...prevState,
            errors,
          }));
        } else {
          currentNotificationSettings = data;
          // console.log("Before mod: ", data);
        }
      });
      currentNotificationSettings.emailAddresses = [info.Email];

      Object.keys(currentNotificationSettings).forEach(key => {
        if (currentNotificationSettings[key].advancedEmailAddresses) {
          for (let index in currentNotificationSettings[key]
            .advancedEmailAddresses) {
            currentNotificationSettings[key].advancedEmailAddresses[index] =
              info.Email;
          }
        }
      });

      //UPDATE-NOTIICATION-ENDPOINT
      // console.log("After mod: ", currentNotificationSettings);

      const putResponse = await RingCentralService.updateNotificationSetting(
        currentNotificationSettings,
        accountId,
        info.MailboxID.toString(),
        getJWKSToken()
      );

      handleJwksResponse(putResponse, (data, error) => {
        if (error) {
          let errors = state.errors;
          if (error.length && !error[0]?.message?.includes('Internal')) {
            errors.push({
              message: `Notifications failed for ${info.Extension} : ${error[0]?.message}`,
              row: i + 1,
            });
          }
          setState(prevState => ({
            ...prevState,
            errors,
          }));
        } else {
          // console.log("Notifications Changed for " + info.Extension.toString());
        }
      });

      //UPDATE PROGRESS BAR
      let percent = ((i + 1) / extensionData.length) * 100;
      if (percent === 100 && state.errors.length) {
        setState(prevState => ({
          ...prevState,
          showErrorModal: true,
          uploadedPercentage: percent,
        }));
      } else if (percent === 100 && !state.errors.length) {
        setState(prevState => ({
          ...prevState,
          showSuccessModal: true,
          uploadedPercentage: percent,
        }));
      } else {
        setState(prevState => ({
          ...prevState,
          uploadedPercentage: percent,
        }));
      }
    }
  };

  const handleFileSelection = event => {
    let selectedFile = null;
    if (event.currentTarget.files && event.currentTarget.files.length > 0) {
      selectedFile = event.currentTarget.files[0];
    }

    if (!selectedFile) {
      return;
    }

    setState(prevState => ({
      ...prevState,
      selectedFile,
    }));
  };

  const handleErrorDownload = () => {
    if (state.errors.length) {
      const dataToDownload = state.errors.map(error => {
        return {
          Error: error.message,
          RowNumber: error.row,
        };
      });
      writeXLSFile('ExtensionActivator_Errors.xlsx', 'sheet', dataToDownload, {
        bookType: 'xlsx',
        raw: false,
      });
    }
  };

  const validateFile = (headersList = []) => {
    let isFileCorrect = true;
    COLUMN_HEADERS.map(columnName => {
      if (!headersList.includes(columnName)) {
        isFileCorrect = false;
      }
    });
    return isFileCorrect;
  };

  const handleUpload = async () => {
    const fileContent = await getFileContent(state.selectedFile, { header: 1 });
    const sheetData = getSheetData(fileContent, fileContent.SheetNames[0]);
    const dataBeingUploaded = mapFileContent(sheetData);
    const isValidFile = validateFile(sheetData[0]);
    if (
      !dataBeingUploaded.length ||
      !fileContent ||
      Object.keys(fileContent).length === 0
    ) {
      return toast.error('Selected file is empty');
    }
    if (!isValidFile) {
      return toast.error('File content is invalid');
    }
    shouldSyncPause = false;
    setState(prevState => ({
      ...prevState,
      errors: [],
      pageSize: 20,
      pageNumber: 1,
      sortObj: null,
      showProgressBar: false,
      isSyncingRecords: false,
      isErrorExpanded: true,
      dataBeingUploaded,
      openUploadDialog: false,
      selectedFile: null,
      uploadedPercentage: 0,
    }));
    getPreparedData(
      defaultState.pageSize,
      defaultState.pageNumber,
      dataBeingUploaded
    );
  };

  const getPreparedData = (
    pageSize = state.pageSize,
    pageNumber = state.pageNumber,
    dataBeingUploaded = [],
    sort = state.sortObj
  ) => {
    setState(prevState => ({
      ...prevState,
      isLoading: true,
    }));
    setTimeout(() => {
      let preparedData = dataBeingUploaded.length
        ? dataBeingUploaded
        : state.dataBeingUploaded;

      let sortObj = sort ? sort : { field: 'MailboxID', order: 'ASC' };

      preparedData.sort((rowA, rowB) => {
        if (NUMBER_TYPE_COLUMNS.includes(sortObj.field)) {
          if (sortObj.order === 'ASC') {
            return rowA[sortObj.field] - rowB[sortObj.field];
          } else {
            return rowB[sortObj.field] - rowA[sortObj.field];
          }
        } else {
          let value1 = BOOLEAN_TYPE_COLUMNS.includes(sortObj.field)
            ? String(rowA[sortObj.field])?.toLowerCase()
            : rowA[sortObj.field]?.toLowerCase();
          let value2 = BOOLEAN_TYPE_COLUMNS.includes(sortObj.field)
            ? String(rowB[sortObj.field])?.toLowerCase()
            : rowB[sortObj.field]?.toLowerCase();

          if (sortObj.order === 'ASC') {
            return value1 > value2 ? 1 : -1;
          } else {
            return value2 > value1 ? 1 : -1;
          }
        }
      });

      if (pageSize !== -1) {
        preparedData = preparedData.slice(
          (pageNumber - 1) * pageSize,
          (pageNumber - 1) * pageSize + pageSize
        );
      }

      setState(prevState => ({
        ...prevState,
        isLoading: false,
        gridData: preparedData,
        pageNumber,
        pageSize,
        sortObj: sort,
      }));
    }, 600);
  };

  const isButtonDisabled =
    state.isSyncingRecords &&
    state.uploadedPercentage !== 100 &&
    !shouldSyncPause;

  //JSX
  return (
    <>
      <Paper
        className={clsx(
          'd-flex f-justify-center flex-column mr-10 ml-10 p-6 mt-5'
        )}
        elevation={paperElevation}
      >
        <div className='d-flex f-justify-between'>
          <div>
            <Typography variant='h6' className='bold-font'>
              File Upload
            </Typography>
            <Typography variant='body2' className='mt-3'>
              4. Upload your edited file.
            </Typography>
          </div>
          <div>
            {state.isSyncingRecords && state.uploadedPercentage !== 100 && (
              <Tooltip title='Stop' placement='top'>
                <Button
                  variant='contained'
                  color='primary'
                  className={clsx('mr-4', classes.syncButton)}
                  disabled={shouldSyncPause}
                  onClick={() => {
                    shouldSyncPause = true;
                  }}
                >
                  <AlbumIcon />
                </Button>
              </Tooltip>
            )}
            <Button
              variant='contained'
              color='primary'
              onClick={() => {
                setState(prevState => ({
                  ...prevState,
                  openUploadDialog: true,
                }));
              }}
              classes={{
                startIcon: 'mr-0',
              }}
              disabled={isButtonDisabled}
              startIcon={<CloudUpload className='mr-2' />}
            >
              Upload File
            </Button>
            {/* <Button
              variant='contained'
              color='primary'
              onClick={() => {
                shouldSyncPause = false;
                setState(defaultState);
                onReset();
              }}
              disabled={isButtonDisabled}
            >
              Reset
            </Button> */}
          </div>
        </div>
        {state.showProgressBar && (
          <div className='mt-4'>
            <Typography
              variant='body1'
              className='d-flex f-justify-center bold-font mb-4'
            >
              {!!state.errors.length
                ? 'FAILED TO SYNC'
                : 'EXTENSIONS UPLOADING'}
            </Typography>
            <LinearProgress
              classes={{
                barColorPrimary: state.errors.length
                  ? classes.errorColor
                  : classes.successColor,
              }}
              className={clsx(classes.progressBar, 'mt-2')}
              variant='determinate'
              value={state.uploadedPercentage}
            />
            <Typography variant='body2' className='color-text-secondary mt-1'>
              {`${Math.floor(state.uploadedPercentage)}%`}
            </Typography>
            {!!state.errors.length && (
              <ErrorList
                isExpanded={state.isErrorExpanded}
                onToggleIcon={() => {
                  setState(prevState => ({
                    ...prevState,
                    isErrorExpanded: !prevState.isErrorExpanded,
                  }));
                }}
                onDownloadClick={handleErrorDownload}
                errors={state.errors}
                isDisabled={isButtonDisabled}
              />
            )}
          </div>
        )}
      </Paper>
      <UploadDialog
        isOpen={state.openUploadDialog}
        selectedFile={state.selectedFile}
        onClose={() => {
          setState(prevState => ({
            ...prevState,
            openUploadDialog: false,
            selectedFile: null,
          }));
        }}
        onChange={handleFileSelection}
        handleUpload={handleUpload}
      />
      {!!state.dataBeingUploaded.length && (
        <ExtensionGrid
          pageSize={state.pageSize}
          pageNumber={state.pageNumber}
          onPageSizeChange={pageSize => {
            getPreparedData(pageSize, 1);
          }}
          onPageNumberChange={pageNumber => {
            getPreparedData(state.pageSize, pageNumber);
          }}
          total={state.dataBeingUploaded.length}
          gridData={state.gridData}
          isLoading={state.isLoading}
          isSyncingRecords={state.isSyncingRecords}
          onSyncConfirm={onSyncConfirm}
          sortObj={state.sortObj}
          handleSortChange={(field, order) => {
            let sortObj = order ? { field, order } : null;
            getPreparedData(
              state.pageSize,
              state.pageNumber,
              state.dataBeingUploaded,
              sortObj
            );
          }}
        />
      )}
      <SyncSuccessComponent
        isOpen={state.showSuccessModal}
        onClose={() => {
          setState(prevState => ({
            ...prevState,
            showSuccessModal: false,
          }));
        }}
        onReset={() => {
          setState(defaultState);
          onReset();
        }}
      />
      <SyncErrorComponent
        isOpen={state.showErrorModal}
        onClose={() => {
          setState(prevState => ({
            ...prevState,
            showErrorModal: false,
          }));
        }}
        onReset={() => {
          setState(defaultState);
          onReset();
        }}
      />
    </>
  );
};
export default UploadData;
